/**
 * Bug Reporter Widget
 * Entry point for embeddable bug reporting widget
 *
 * Provides global API for initializing and managing the widget.
 * Usage: window.BugReporter.init({ apiKey, apiBaseUrl, turnstileSiteKey, ... })
 *
 * @packageDocumentation
 */

import { createRoot, type Root } from 'react-dom/client';
import { z } from 'zod';
import { FloatingButton } from './FloatingButton';
import { DEFAULT_WIDGET_CONFIG, type WidgetConfig } from '@wds/bug-reporter-types';
import { sanitizeSvg } from './utils/svg-sanitizer';
import './styles.css';

// DEBUG: Log immediately on script load
console.log('[BugWidget] Script ||| loaded v2025-01-14T00:15 (fixed CSS vars + drag threshold + padding)');

/**
 * Widget configuration schema (Zod validation)
 */
const ConfigSchema = z.object({
  /** API key for authentication with bug reporter backend */
  apiKey: z.string().min(1, 'API key is required'),

  /** Base URL for bug reporter API (e.g., https://api.example.com) */
  apiBaseUrl: z.string().url('Invalid API base URL'),

  /** Cloudflare Turnstile site key for bot protection */
  turnstileSiteKey: z.string().min(1, 'Turnstile site key is required'),

  /** Widget position on page */
  position: z
    .enum(['bottom-right', 'bottom-left', 'top-right', 'top-left'])
    .optional(),

  /** Brand color for widget (CSS color value) */
  brandColor: z.string().optional(),

  /** Widget button text */
  buttonText: z.string().optional(),

  /** Widget button tooltip */
  buttonTooltip: z.string().optional(),

  /** SVG logo for widget button */
  logoSvg: z.string().optional(),

  /** Show button shadow */
  buttonShadow: z.boolean().optional(),

  /** Button border color */
  buttonBorderColor: z.string().optional(),

  /** Hover effect type */
  hoverEffect: z.enum(['no-effect', 'glow', 'shadow']).optional(),

  /** Enable hover scale animation */
  hoverScale: z.boolean().optional(),

  /** Icon scale multiplier */
  iconScale: z.number().optional(),

  /** Success message after submission */
  successMessage: z.string().optional(),

  /** Required fields for bug report */
  requiredFields: z.array(z.enum(['email', 'screenshot'])).optional(),

  /** Minimum description length */
  minDescriptionLength: z.number().optional(),

  /** Maximum description length */
  maxDescriptionLength: z.number().optional(),

  /** Screenshot retention days */
  screenshotRetentionDays: z.number().optional(),
});

/**
 * Widget configuration interface
 */
export type BugReporterConfig = z.infer<typeof ConfigSchema>;

/**
 * Internal widget state (singleton pattern)
 */
class BugReporterWidget {
  private config: BugReporterConfig | null = null;
  private root: Root | null = null;
  private containerElement: HTMLDivElement | null = null;
  private initialized = false;

  /**
   * Get widget styles for Shadow DOM injection
   * Returns the main CSS content that needs to be scoped to the widget
   */
  private getWidgetStyles(): string {
    return `
      /* Entrance animation - smooth fade-in from bottom */
      @keyframes bug-reporter-entrance {
        0% {
          opacity: 0;
          transform: translateY(20px) scale(0.8);
        }
        100% {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      /* Floating Button */
      .bug-reporter-button {
        position: fixed;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background-color: var(--bug-reporter-brand-color);
        color: white;
        border: none;
        box-shadow: var(--bug-reporter-shadow-lg);
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        z-index: 999999;
        outline: none;
        animation: bug-reporter-entrance 0.4s ease-out forwards;
      }

      @media (prefers-reduced-motion: reduce) {
        .bug-reporter-button {
          animation: none;
        }
      }

      @media (max-width: 768px) {
        .bug-reporter-button {
          width: 48px;
          height: 48px;
        }
      }

      .bug-reporter-button:hover {
        transform: scale(1.05);
        box-shadow: var(--bug-reporter-shadow-xl);
      }

      .bug-reporter-button:active {
        transform: scale(0.95);
      }

      .bug-reporter-button:focus {
        outline: 2px solid var(--bug-reporter-brand-color);
        outline-offset: 2px;
      }

      /* Position variants */
      .bug-reporter-button--bottom-right {
        bottom: 24px;
        right: 24px;
      }

      .bug-reporter-button--bottom-left {
        bottom: 24px;
        left: 24px;
      }

      .bug-reporter-button--top-right {
        top: 24px;
        right: 24px;
      }

      .bug-reporter-button--top-left {
        top: 24px;
        left: 24px;
      }

      @media (max-width: 768px) {
        .bug-reporter-button--bottom-right {
          bottom: 16px;
          right: 16px;
        }

        .bug-reporter-button--bottom-left {
          bottom: 16px;
          left: 16px;
        }

        .bug-reporter-button--top-right {
          top: 16px;
          right: 16px;
        }

        .bug-reporter-button--top-left {
          top: 16px;
          left: 16px;
        }
      }

      /* Button icon */
      .bug-reporter-button__icon {
        width: 24px;
        height: 24px;
        position: relative;
        z-index: 1;
      }

      .bug-reporter-button__icon svg {
        width: 100% !important;
        height: 100% !important;
        display: block;
      }

      /* Button text */
      .bug-reporter-button__text {
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
        position: relative;
        z-index: 1;
      }

      /* Button with text becomes pill-shaped */
      .bug-reporter-button:has(.bug-reporter-button__text) {
        width: auto;
        border-radius: 28px;
        padding: 0 20px;
        gap: 8px;
      }

      /* Pulse animation ring */
      .bug-reporter-button__pulse {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: inherit;
        background-color: var(--bug-reporter-brand-color);
        opacity: 0.6;
        animation: bug-reporter-pulse 2s ease-out infinite;
      }

      @keyframes bug-reporter-pulse {
        0% {
          transform: scale(1);
          opacity: 0.6;
        }
        70% {
          transform: scale(1.3);
          opacity: 0;
        }
        100% {
          transform: scale(1);
          opacity: 0;
        }
      }

      /* Dialog Overlay */
      .bug-reporter-dialog-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: var(--bug-reporter-bg-overlay);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000000;
        padding: 16px;
        animation: bug-reporter-fade-in 0.2s ease;
      }

      @keyframes bug-reporter-fade-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      /* Dialog */
      .bug-reporter-dialog {
        background-color: var(--bug-reporter-bg-dialog);
        border-radius: 8px;
        box-shadow: var(--bug-reporter-shadow-xl);
        max-width: 1100px;
        width: 100%;
        max-height: 90vh;
        display: flex;
        flex-direction: column;
        animation: bug-reporter-slide-up 0.3s ease;
      }

      @keyframes bug-reporter-slide-up {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @media (max-width: 768px) {
        .bug-reporter-dialog {
          max-width: 100%;
          max-height: 95vh;
        }
      }

      /* Dialog Header */
      .bug-reporter-dialog__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px 24px;
        border-bottom: 1px solid var(--bug-reporter-border-color);
        background-color: var(--bug-reporter-brand-color);
        color: white;
      }

      @media (max-width: 768px) {
        .bug-reporter-dialog__header {
          padding: 16px 20px;
        }
      }

      .bug-reporter-dialog__title {
        font-size: 20px;
        font-weight: 600;
        color: inherit;
        margin: 0;
      }

      @media (max-width: 768px) {
        .bug-reporter-dialog__title {
          font-size: 18px;
        }
      }

      .bug-reporter-dialog__close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 4px;
        background-color: transparent;
        color: inherit;
        border: none;
        cursor: pointer;
        transition: background-color 0.2s ease;
        padding: 0;
      }

      .bug-reporter-dialog__close:hover {
        background-color: rgba(255, 255, 255, 0.2);
      }

      .bug-reporter-dialog__close:focus {
        outline: 2px solid var(--bug-reporter-brand-color);
        outline-offset: 2px;
      }

      .bug-reporter-dialog__close svg {
        width: 20px;
        height: 20px;
      }

      [data-bug-reporter-logo] {
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      [data-bug-reporter-logo] svg {
        height: 32px;
        width: auto;
        color: white;
        fill: currentColor;
      }

      /* Dialog Body */
      .bug-reporter-dialog__body {
        padding: 24px;
        overflow-y: auto;
        flex: 1;
        min-height: 0;
      }

      @media (max-width: 768px) {
        .bug-reporter-dialog__body {
          padding: 20px;
        }
      }

      /* Form Elements */
      .bug-reporter-form {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
      }

      .bug-reporter-form__left-column,
      .bug-reporter-form__right-column {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .bug-reporter-form__full-width {
        grid-column: 1 / -1;
      }

      @media (max-width: 768px) {
        .bug-reporter-form {
          grid-template-columns: 1fr;
          gap: 16px;
        }
      }

      .bug-reporter-form__group {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .bug-reporter-form__label {
        font-size: 14px;
        font-weight: 500;
        color: var(--bug-reporter-text-primary);
      }

      .bug-reporter-form__input,
      .bug-reporter-form__textarea {
        padding: 10px 12px;
        font-size: 14px;
        border: 1px solid var(--bug-reporter-border-color);
        border-radius: 6px;
        background-color: white;
        color: var(--bug-reporter-text-primary);
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
        outline: none;
        min-height: 48px;
        font-family: inherit;
      }

      .bug-reporter-form__input:focus,
      .bug-reporter-form__textarea:focus {
        border-color: var(--bug-reporter-brand-color);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      .bug-reporter-form__textarea {
        min-height: 100px;
        resize: vertical;
      }

      .bug-reporter-form__error {
        font-size: 13px;
        color: #ef4444;
        margin-top: 4px;
      }

      .bug-reporter-form__button {
        padding: 12px 20px;
        font-size: 14px;
        font-weight: 500;
        color: white;
        background-color: var(--bug-reporter-brand-color);
        border: none;
        border-radius: 6px;
        cursor: pointer;
        transition: background-color 0.2s ease, transform 0.1s ease;
        min-height: 48px;
        outline: none;
        font-family: inherit;
      }

      .bug-reporter-form__button:hover {
        background-color: #2563eb;
      }

      .bug-reporter-form__button:active {
        transform: scale(0.98);
      }

      .bug-reporter-form__button:focus {
        outline: 2px solid var(--bug-reporter-brand-color);
        outline-offset: 2px;
      }

      .bug-reporter-form__button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .bug-reporter-form__button--secondary {
        background-color: #f3f4f6;
        color: var(--bug-reporter-text-primary);
      }

      .bug-reporter-form__button--secondary:hover {
        background-color: #e5e7eb;
      }

      .bug-reporter-form__counter {
        font-size: 12px;
        color: #6b7280;
      }

      .bug-reporter-form__counter--error {
        color: #ef4444;
      }

      /* Screenshot Canvas Toolbar */
      .bug-reporter-screenshot-toolbar {
        flex-wrap: wrap;
        max-width: calc(100vw - 32px);
      }

      .bug-reporter-tool-btn {
        min-width: 36px;
        min-height: 36px;
        touch-action: manipulation;
      }

      @media (max-width: 768px) {
        .bug-reporter-screenshot-toolbar {
          top: 8px !important;
          padding: 8px !important;
          gap: 4px !important;
          max-width: calc(100vw - 16px);
        }

        .bug-reporter-tool-btn {
          padding: 6px !important;
          min-width: 32px;
          min-height: 32px;
        }

        .bug-reporter-tool-btn svg {
          width: 16px !important;
          height: 16px !important;
        }

        .bug-reporter-screenshot-toolbar input[type="color"] {
          width: 32px !important;
          height: 32px !important;
        }

        .bug-reporter-screenshot-toolbar button[style*="padding: 8px 16px"] {
          padding: 6px 12px !important;
          font-size: 13px !important;
        }
      }

      @media (max-width: 480px) {
        .bug-reporter-screenshot-toolbar {
          flex-wrap: wrap;
          justify-content: center;
          gap: 6px !important;
          padding: 10px !important;
          max-width: calc(100vw - 16px);
        }

        .bug-reporter-tool-btn {
          padding: 8px !important;
          min-width: 36px;
          min-height: 36px;
        }

        .bug-reporter-tool-btn svg {
          width: 18px !important;
          height: 18px !important;
        }

        .bug-reporter-screenshot-toolbar > div[style*="width: 1px"] {
          display: none !important;
        }

        .bug-reporter-screenshot-toolbar input[type="color"] {
          width: 36px !important;
          height: 36px !important;
        }

        .bug-reporter-screenshot-toolbar button[style*="padding: 8px 16px"] {
          padding: 8px 12px !important;
          font-size: 14px !important;
          min-height: 36px;
        }
      }
    `;
  }

  /**
   * Initialize the bug reporter widget
   *
   * @param config - Widget configuration
   * @throws {Error} If configuration is invalid
   *
   * @example
   * ```typescript
   * window.BugReporter.init({
   *   apiKey: 'bw_live_abc123...',
   *   apiBaseUrl: 'https://api.example.com',
   *   turnstileSiteKey: 'your-site-key',
   *   position: 'bottom-right',
   *   brandColor: '#3b82f6'
   * });
   * ```
   */
  public async init(config: BugReporterConfig): Promise<void> {
    // Validate configuration
    const result = ConfigSchema.safeParse(config);
    if (!result.success) {
      const messages = result.error.issues.map((err) => `${err.path.join('.')}: ${err.message}`);
      throw new Error(`Invalid configuration:\n${messages.join('\n')}`);
    }
    this.config = result.data;

    // Handle multiple init() calls (singleton pattern)
    if (this.initialized) {
      console.warn('[BugReporter] Widget already initialized. Updating configuration...');
      this.destroy();
    }

    // Build merged config from embed code parameters only (no API fetch)
    // All configuration is passed directly via window.BugReporter.init()
    const mergedConfig: WidgetConfig = {
      brandColor: this.config.brandColor ?? '#3b82f6',
      logoSvg: this.config.logoSvg ?? null,
      buttonPosition: (this.config.position ?? 'bottom-right') as any,
      buttonText: this.config.buttonText ?? DEFAULT_WIDGET_CONFIG.buttonText,
      buttonTooltip: this.config.buttonTooltip ?? null,
      buttonShadow: this.config.buttonShadow ?? true,
      buttonBorderColor: this.config.buttonBorderColor ?? 'transparent',
      hoverEffect: (this.config.hoverEffect ?? 'no-effect') as any,
      hoverScale: this.config.hoverScale ?? true,
      iconScale: this.config.iconScale ?? 1.0,
      successMessage: this.config.successMessage ?? DEFAULT_WIDGET_CONFIG.successMessage,
      requiredFields: (this.config.requiredFields as any) ?? DEFAULT_WIDGET_CONFIG.requiredFields,
      minDescriptionLength: this.config.minDescriptionLength ?? DEFAULT_WIDGET_CONFIG.minDescriptionLength,
      maxDescriptionLength: this.config.maxDescriptionLength ?? DEFAULT_WIDGET_CONFIG.maxDescriptionLength,
      screenshotRetentionDays: this.config.screenshotRetentionDays ?? DEFAULT_WIDGET_CONFIG.screenshotRetentionDays,
    };

    // Debug: Log merged config
    console.log('[BugReporter] Merged config:', mergedConfig);

    // Sanitize SVG logo if present
    if (mergedConfig.logoSvg) {
      const sanitizedSvg = sanitizeSvg(mergedConfig.logoSvg);
      if (!sanitizedSvg) {
        console.warn('[BugReporter] Failed to sanitize SVG logo, using default icon');
        mergedConfig.logoSvg = null;
      } else {
        mergedConfig.logoSvg = sanitizedSvg;
      }
    }

    // Create host element for Shadow DOM
    const hostElement = document.createElement('div');
    hostElement.id = 'bug-reporter-widget-host';
    document.body.appendChild(hostElement);

    // Create a click bridge button in Light DOM to handle clicks from browser automation tools
    // that can't directly click Shadow DOM elements (like Playwright)
    const bridgeButton = document.createElement('button');
    bridgeButton.id = 'bug-reporter-button-bridge';
    bridgeButton.style.position = 'fixed';
    bridgeButton.style.bottom = '24px';
    bridgeButton.style.right = '24px';
    bridgeButton.style.width = '0px';
    bridgeButton.style.height = '0px';
    bridgeButton.style.padding = '28px'; // Invisible but clickable area (56px total)
    bridgeButton.style.border = 'none';
    bridgeButton.style.background = 'transparent';
    bridgeButton.style.cursor = 'pointer';
    bridgeButton.style.zIndex = '999999';
    bridgeButton.style.pointerEvents = 'auto';
    bridgeButton.setAttribute('aria-hidden', 'true');
    bridgeButton.setAttribute('tabindex', '-1');

    // When bridge button is clicked, click the real button in Shadow DOM
    bridgeButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const shadowRoot = hostElement.shadowRoot;
      if (shadowRoot) {
        const realButton = shadowRoot.querySelector('button');
        if (realButton) {
          realButton.click();
        }
      }
    });

    document.body.appendChild(bridgeButton);

    // Attach Shadow DOM for style encapsulation
    const shadowRoot = hostElement.attachShadow({ mode: 'open' });

    // Create container element inside Shadow DOM
    this.containerElement = document.createElement('div');
    this.containerElement.id = 'bug-reporter-widget-root';
    this.containerElement.setAttribute('data-bug-reporter', 'true');

    // Apply CSS variable for brand color
    this.containerElement.style.setProperty('--bug-reporter-brand-color', mergedConfig.brandColor);

    // Create style element for Shadow DOM with CSS variables and base styles
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      :host {
        --bug-reporter-brand-color: ${mergedConfig.brandColor};
        --bug-reporter-text-primary: #1f2937;
        --bug-reporter-text-secondary: #6b7280;
        --bug-reporter-bg-overlay: rgba(0, 0, 0, 0.5);
        --bug-reporter-bg-dialog: #ffffff;
        --bug-reporter-border-color: #e5e7eb;
        --bug-reporter-shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        --bug-reporter-shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        --bug-reporter-shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        --bug-reporter-shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        display: contents; /* Allows button to be part of normal flow */
      }

      #bug-reporter-widget-root {
        /* Note: Don't use 'all: initial' - it breaks CSS variable inheritance! */
        /* Shadow DOM already provides complete style isolation from host page */
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        font-size: 16px;
        line-height: 1.5;
        color: var(--bug-reporter-text-primary);
        box-sizing: border-box;
      }

      #bug-reporter-widget-root * {
        box-sizing: border-box;
        margin: 0;
        /* Note: Don't reset padding here - it breaks component styles! */
        /* padding is intentionally set by individual component CSS rules */
      }
    `;

    // Append styles to shadow root
    shadowRoot.appendChild(styleElement);

    // Inject the main widget CSS into Shadow DOM
    // This ensures all widget styles are scoped to the shadow root
    const linkElement = document.createElement('style');
    linkElement.textContent = this.getWidgetStyles();
    shadowRoot.appendChild(linkElement);

    // Append container to shadow root
    shadowRoot.appendChild(this.containerElement);

    // Initialize React root with container inside Shadow DOM
    this.root = createRoot(this.containerElement);

    // Render FloatingButton component with merged config
    this.root.render(
      <FloatingButton
        position={mergedConfig.buttonPosition}
        brandColor={mergedConfig.brandColor}
        logoSvg={mergedConfig.logoSvg}
        buttonText={mergedConfig.buttonText}
        apiKey={this.config.apiKey}
        apiBaseUrl={this.config.apiBaseUrl}
        turnstileSiteKey={this.config.turnstileSiteKey}
        widgetConfig={mergedConfig}
      />
    );

    this.initialized = true;

    console.log('[BugReporter] Widget initialized successfully', {
      position: mergedConfig.buttonPosition,
      brandColor: mergedConfig.brandColor,
      hasLogo: !!mergedConfig.logoSvg,
      buttonText: mergedConfig.buttonText,
    });
  }

  /**
   * Destroy widget instance (cleanup)
   *
   * @internal
   */
  private destroy(): void {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }

    if (this.containerElement) {
      // Find and remove the Shadow DOM host element
      const hostElement = document.getElementById('bug-reporter-widget-host');
      if (hostElement && hostElement.parentNode) {
        hostElement.parentNode.removeChild(hostElement);
      }
      this.containerElement = null;
    }

    // Remove the bridge button if it exists
    const bridgeButton = document.getElementById('bug-reporter-button-bridge');
    if (bridgeButton && bridgeButton.parentNode) {
      bridgeButton.parentNode.removeChild(bridgeButton);
    }

    this.initialized = false;
  }

  /**
   * Check if widget is initialized
   *
   * @returns True if widget is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get current configuration
   *
   * @returns Current configuration or null if not initialized
   */
  public getConfig(): BugReporterConfig | null {
    return this.config;
  }
}

// Create singleton instance
const widgetInstance = new BugReporterWidget();

/**
 * Global API exposed on window.BugReporter
 */
export const BugReporter = {
  /**
   * Initialize widget with configuration
   */
  init: (config: BugReporterConfig) => widgetInstance.init(config),

  /**
   * Check if widget is initialized
   */
  isInitialized: () => widgetInstance.isInitialized(),

  /**
   * Get current configuration
   */
  getConfig: () => widgetInstance.getConfig(),
};

// Expose global API (only in browser environment)
if (typeof window !== 'undefined') {
  (window as any).BugReporter = BugReporter;

  // Auto-initialize from window.BugReporterConfig if present
  // This allows embed code to simply set window.BugReporterConfig = {...}
  // and the widget will automatically initialize
  if ((window as any).BugReporterConfig) {
    // Use setTimeout to ensure DOM is fully ready
    const config = (window as any).BugReporterConfig as BugReporterConfig;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        widgetInstance.init(config).catch((error) => {
          console.error('[BugReporter] Auto-init failed:', error);
        });
      });
    } else {
      // DOM already loaded, initialize immediately
      widgetInstance.init(config).catch((error) => {
        console.error('[BugReporter] Auto-init failed:', error);
      });
    }
  }
}

/**
 * Export TypeScript types for consumers
 */
export type { SubmitBugReportRequest, SubmitBugReportResponse } from '@wds/bug-reporter-types';
